import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { appSupportService } from "@/features/app-support/services/appSupportService";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import {
  CATEGORY_OPTIONS,
  SEVERITY_OPTIONS,
  type AppSupportCategory,
  type AppSupportSeverity,
} from "@/features/app-support/types/appSupport";

export const AppSupportFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title:       "",
    description: "",
    category:    "" as AppSupportCategory | "",
    severity:    "NORMAL" as AppSupportSeverity,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError(t("app_support.error_title_required"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await appSupportService.createTicket({
        title:       form.title,
        description: form.description || undefined,
        category:    form.category || null,
        severity:    form.severity,
      });
      navigate("/support");
    } catch {
      setError(t("app_support.error_submit_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/support")}
          className="text-sm text-indigo-600 hover:underline flex items-center gap-1 mb-2"
        >
          ← {t("common.back")}
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t("app_support.new_request")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("app_support.new_request_subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("app_support.category")}
          </label>
          <select
            value={form.category}
            onChange={(e) => handleChange("category", e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">{t("common.select")}</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
            ))}
          </select>
        </div>

        {/* Severity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("app_support.severity")}
          </label>
          <select
            value={form.severity}
            onChange={(e) => handleChange("severity", e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {SEVERITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
            ))}
          </select>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("app_support.title_label")} <span className="text-red-500">*</span>
          </label>
          <Input
            value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
            placeholder={t("app_support.title_placeholder")}
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("app_support.description")}
          </label>
          <textarea
            value={form.description}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={5}
            placeholder={t("app_support.description_placeholder")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
          />
        </div>

        <div className="flex gap-3 justify-end pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/support")}
            disabled={saving}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? t("common.loading") : t("app_support.submit_request")}
          </Button>
        </div>
      </form>
    </div>
  );
};
